import classNames from 'classnames';
import React from 'react';
import type { TabBarMenuItem, TabIcon } from './shared-types';
import * as styles from './TabBarMenu.module.scss';

import DropdownMenu from '../components/DropdownMenu';
import SelectEntityOption from '../components/SelectEntityOption';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import { PostType } from '../graphql/generated/graphql';
import GroupIcon from '../groups/GroupIcon';
import cloudSVG from '../icons/non-prefilled/tabbar/cloud.svg';
import homeSVG from '../icons/prefilled/16x16/home.svg';
import { PostIconForState } from '../post/PostIcon';
import { TopicEntityIcon } from '../topic/TopicIcon';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';

const iconTypes: Record<TabIcon, React.ReactNode> = {
  cloud: (
    <SVGIcon className={classNames(styles.entityIcon, styles.cloud)}>
      {cloudSVG}
    </SVGIcon>
  ),
  home: (
    <SVGIcon className={classNames(styles.entityIcon, styles.home)}>
      {homeSVG}
    </SVGIcon>
  ),
  post: (
    <PostIconForState
      state={{ type: PostType.Post, draft: false, archived: false }}
      prefilled
    />
  ),
  topic: <TopicEntityIcon />,
  template: (
    <PostIconForState
      state={{ type: PostType.Template, draft: false, archived: false }}
      prefilled
    />
  ),
  drafts: (
    <PostIconForState
      state={{ type: PostType.Post, draft: true, archived: false }}
      prefilled
    />
  ),

  group: <GroupIcon />,
};

interface TabBarMenuProps {
  openTabs: TabBarMenuItem[];
  recentTabs: TabBarMenuItem[];
  onSelect: (tab: TabBarMenuItem) => void;
  onClose: () => void;
}

const TabBarMenu = ({
  openTabs,
  recentTabs,
  onSelect,
  onClose,
}: TabBarMenuProps) => {
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  const getIcon = (tab: TabBarMenuItem) =>
    tab.iconUrl ? (
      <img src={tab.iconUrl} className={styles.avatarIcon} alt="" />
    ) : (
      iconTypes[tab.icon] || null
    );

  return (
    <ClickOutsideHandler asChild onClickOutside={onClose}>
      <div className={styles.container}>
        <DropdownMenu.Root
          inputContainer={null}
          sections={[
            {
              label: 'Open tabs',
              items: openTabs.map((tab) => ({
                id: tab.id.toString(),
                data: {
                  label: tab.title,
                  icon: getIcon(tab),
                  secondary: tab.isCurrent ? (
                    'Currently open'
                  ) : (
                    <>
                      Viewed <Timestamp date={tab.lastActivityTime} />
                    </>
                  ),
                },
              })),
            },
            {
              label: 'Recently closed',
              items: recentTabs.map((tab) => ({
                id: tab.id.toString(),
                data: {
                  label: tab.title,
                  icon: getIcon(tab),
                  secondary: <Timestamp date={tab.lastActivityTime} />,
                },
              })),
            },
          ].filter((section) => section.items.length > 0)}
          index={focusedIndex}
          onIndexChange={setFocusedIndex}
          onSelect={(item) => {
            const selectedTab = [...openTabs, ...recentTabs].find(
              (tab) => tab.id.toString() === item.id,
            );
            if (selectedTab) {
              onSelect(selectedTab);
            }
          }}
          onClose={onClose}
          maxHeight="75vh"
          renderItem={(data) => {
            return <SelectEntityOption {...data} icon={data.icon} />;
          }}
        />
      </div>
    </ClickOutsideHandler>
  );
};

export default TabBarMenu;
